import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogProps,
  useMediaQuery,
  Theme,
  Slide,
} from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import { forwardRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { PartnerForm } from './partner-form'
import {
  partnerDefaultValues,
  PartnerFormData,
  partnerFormSchema,
} from './partner-form.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCreatePartner } from '@/api/accounting-dealer'
import { toast } from '@/helpers'
import { useCompany } from '@/context/companies'
import { LoadingButton } from '@mui/lab'
import { useQueryClient } from '@tanstack/react-query'

type AddPartnerDialogProps = DialogProps & {
  onClose: () => void
}

export function AddPartnerDialog(props: AddPartnerDialogProps) {
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'))

  const { company } = useCompany()

  const queryClient = useQueryClient()

  const form = useForm<PartnerFormData>({
    resolver: zodResolver(partnerFormSchema),
    defaultValues: partnerDefaultValues,
  })

  const { mutate: createPartner, isPending: isCreatingPartner } =
    useCreatePartner({
      onSuccess: () => {
        onClose()
        toast.success('Sócio criado com sucesso')
        queryClient.invalidateQueries({
          queryKey: ['company', company.key],
        })
      },
    })

  const handleSubmit = (data: PartnerFormData) => {
    createPartner({
      companyKey: company.key,
      partnerData: data,
    })
  }

  const onClose = () => {
    form.reset()
    props.onClose()
  }

  return (
    <Dialog
      {...props}
      fullScreen={isMobile}
      TransitionComponent={isMobile ? Transition : undefined}
      component="form"
      onSubmit={form.handleSubmit(handleSubmit)}
    >
      <DialogTitle>Adicionar sócio</DialogTitle>

      <DialogContent>
        <FormProvider {...form}>
          <PartnerForm />
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancelar
        </Button>
        <LoadingButton type="submit" loading={isCreatingPartner}>
          Adicionar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

const Transition = forwardRef(
  (props: TransitionProps & { children: React.ReactElement }, ref) => (
    <Slide direction="up" ref={ref} {...props} />
  ),
)
